import React, { useEffect, useState } from 'react';
import { getBlockedEmails, unblockEmail } from 'state/ducks/blocked-emails/operations';
import { localizedDynamic, localizedInterpolation, i18nTranslationKey } from 'state/i18n';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import BiInputText from 'view/shared/components/bi-input-text/bi-input-text';
import { ConfirmDialog } from 'view/shared/components/dialogs/bi-dialog-confirm/dialog-confirm';
import { Spinner } from 'view/components/spinner/spinner';
import BlockedEmailItem from './blocked-email-item';
import './blocked-emails.scss';

export const BlockedEmails: React.FC<{}> = () => {
	const dispatch = useAppDispatch();
	const blockedEmails = useAppSelector(state => state.blockedEmailsReducer.blockedEmails);
	const isLoading = useAppSelector(state => state.blockedEmailsReducer.isLoadingBlockedEmails);

	const [search, setSearch] = useState<string>('');
	const [confirmEmail, setConfirmEmail] = useState<string | null>(null);

	useEffect(() => {
		getBlockedEmails()(dispatch);
	}, [dispatch]);

	const normalizedSearch = search.trim().toLowerCase();
	const filteredEmails = normalizedSearch
		? blockedEmails.filter(b => (b.email ?? '').toLowerCase().indexOf(normalizedSearch) !== -1)
		: blockedEmails;

	const showLoading = isLoading && blockedEmails.length === 0;
	const showEmpty = !showLoading && filteredEmails.length === 0;

	const handleConfirmUnblock = () => {
		if (confirmEmail) {
			unblockEmail(confirmEmail)(dispatch);
		}
		setConfirmEmail(null);
	};

	const handleCancelUnblock = () => setConfirmEmail(null);

	return (
		<div className="blocked-emails-card flex-direction-column flex-fill-width">
			<div className="blocked-emails-search margin-bottom-10px">
				<BiInputText
					value={search}
					placeholder={localizedDynamic('blockedEmails.searchPlaceholder')}
					onChange={e => setSearch((e.target as HTMLInputElement).value)}
				/>
			</div>

			{showLoading ? (
				<Spinner
					shouldOverlay={false}
					spinnerSize="spinner-container-half-size"
					useMarginsForCorrectingCentering={false}
				/>
			) : showEmpty ? (
				<span className="blocked-emails-empty">
					{localizedDynamic(search ? 'blockedEmails.noMatches' : 'blockedEmails.empty')}
				</span>
			) : (
				<div className="blocked-emails-list">
					{filteredEmails.map(item => (
						<BlockedEmailItem
							key={item.email}
							item={item}
							onUnblockClick={() => setConfirmEmail(item.email ?? null)}
						/>
					))}
				</div>
			)}

			{confirmEmail && (
				<ConfirmDialog
					visible={true}
					header={localizedDynamic('blockedEmails.confirmTitle')}
					dialogMessage={localizedInterpolation('blockedEmails.confirmMessage' as i18nTranslationKey, {
						email: confirmEmail,
					})}
					confirmMessage={localizedDynamic('blockedEmails.confirmAction')}
					confirmColor="org-green"
					onHide={handleCancelUnblock}
					onCancel={handleCancelUnblock}
					onConfirm={handleConfirmUnblock}
				/>
			)}
		</div>
	);
};
